import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import axios from "axios";
import { ReactComponent as SucessIcon } from "assets/icons/SucessIcon.svg";
import StripeCard from "components/StripeCard/StripeCard";
import TranzilaCard from "components/TranzilaCard/TranzilaCard";
import ManageCreditsCard from "components/ManageCreditsCard/ManageCreditsCard";
import Recaptcha from "components/Recaptcha/Recaptcha";
import FindMyCity from "components/FindMyCity/FindMyCity";
import TributeInformation from "../ExternalLinkDonation/DonationPay/TributeInformation";
import LoginModal from "./LoginModal";
import FundraisingAPI from "Lib/API/Fundraising";
import FinanceService from "Lib/API/Finance";
import "./DonateModal.scss";
import { FontWeights } from "theme/fontConfig";
import { convenienceFeeAmount as calcConvenienceFee } from "helper/convenienceFee";

const getStoredAccountInfo = () => {
  try { return JSON.parse(localStorage.getItem("accountInfo") || "{}"); } catch { return {}; }
};

const STEPS = ["Amount", "Details", "Payment"];

const DonateModal = ({ campaign, onClose, onDonated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const [donationResult, setDonationResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isValidAccountId = id => !!id && id !== "undefined" && id !== "null";
  const [isAuth, setIsAuth] = useState(() => isValidAccountId(localStorage.getItem("accountID")));
  const [loginOpen, setLoginOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [tranzilaCurrency, setTranzilaCurrency] = useState("ILS");
  const [tranzilaConvertedAmount, setTranzilaConvertedAmount] = useState(null);
  const stripeRef = useRef(null);
  const recaptchaRef = useRef(null);

  const paymentConfig = campaign?.paymentConfig || {};
  const orgCountry = campaign?.orgCountry;

  useEffect(() => {
    if (paymentConfig.currency) setTranzilaCurrency(paymentConfig.currency);
  }, [paymentConfig.currency]);
  const fmtCurrency = (n, decimals = 2) =>
    Number(n || 0).toLocaleString(orgCountry?.local || "en-US", {
      style: "currency", currency: orgCountry?.currency || "USD",
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
  const currSymbol = orgCountry?.currencySymbol || "$";
  const isStripe = paymentConfig.isStripe && paymentConfig.publicKey;
  const isTranzila = paymentConfig.isTranzila;
  const presets = campaign?.preset_amounts?.length ? campaign.preset_amounts : [18, 36, 54, 100, 180, 360];
  const allowOpenAmount = campaign?.allow_open_amount !== false;
  const minimumAmount = campaign?.minimum_amount || 1;
  const hasFee =
    paymentConfig.coverConvenienceFee &&
    paymentConfig.convenienceFeeInfo?.amount > 0;
  const isSingle = campaign?.checkout_style === "single";
  const memberAccountId   = isAuth ? (localStorage.getItem("accountID") || null) : null;
  const memberAccountName = isAuth ? (localStorage.getItem("accountName") || "") : "";
  const canBillAccount = campaign?.allow_bill_account && memberAccountId;

  const fmtCardCurrency = (n, decimals = 2) => {
    const useUSD = isAuth && isTranzila && tranzilaCurrency === "USD" && tranzilaConvertedAmount != null;
    const val = useUSD ? tranzilaConvertedAmount : Number(n || 0);
    const cur = useUSD ? "USD" : (orgCountry?.currency || "USD");
    return val.toLocaleString(orgCountry?.local || "en-US", {
      style: "currency", currency: cur,
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });
  };

  const donateLabel = (amount, interval) => {
    if (!amount) return "Donate";
    const base = fmtCardCurrency(amount);
    if (interval === "month") return `Give ${base}/mo`;
    if (interval === "year")  return `Give ${base}/yr`;
    return `Donate ${base}`;
  };

  const _info = getStoredAccountInfo();
  const formik = useFormik({
    initialValues: {
      presetAmount: "",
      customAmount: "",
      coverFee: false,
      tributeCheck: false,
      tribute_type: { id: "", name: "" },
      donor_name: "",
      note: "",
      showAcknowledgement: false,
      acknowledgements: [{ recipient_name: "", email: "" }],
      first_name: _info.firstName || "",
      last_name: _info.lastName || "",
      receipt_email: _info.primary_email_address || "",
      anonymous: false,
      sendPaper: false,
      addressInfo: { formattedAddress: "", place_id: "" },
      city: "",
      state: "",
      zip_code: "",
      address2: "",
      recurringInterval: "one-time",
      paymentMethod: "card",
    },
  });

  const finalAmount = Number(formik.values.customAmount || formik.values.presetAmount || 0);

  const calcFee = () => {
    if (!hasFee || !finalAmount) return 0;
    return calcConvenienceFee(paymentConfig.convenienceFeeInfo, finalAmount);
  };

  const fee = calcFee();
  const total = formik.values.coverFee ? finalAmount + fee : finalAmount;

  useEffect(() => {
    if (!isAuth || !isTranzila || tranzilaCurrency !== "USD" || !(total > 0) || !paymentConfig.id) {
      setTranzilaConvertedAmount(null);
      return;
    }
    FinanceService.tranzilaHandshakePublic({
      paymentMethodId: paymentConfig.id,
      orgId: campaign?.org_id,
      amount: total,
      currency: "USD",
    })
      .then(res => setTranzilaConvertedAmount(res?.convertedAmount ?? null))
      .catch(() => setTranzilaConvertedAmount(null));
  }, [isAuth, isTranzila, tranzilaCurrency, total, paymentConfig.id, campaign?.org_id]);

  // ── Validation (step-by-step) ────────────────────────────────────────────
  const validateStep = () => {
    if (step === 0) {
      if (!finalAmount) {
        enqueueSnackbar("Please select or enter a donation amount", { variant: "warning" });
        return false;
      }
      if (formik.values.customAmount && Number(formik.values.customAmount) < minimumAmount) {
        enqueueSnackbar(`Minimum custom amount is ${fmtCurrency(minimumAmount, 0)}`, { variant: "warning" });
        return false;
      }
      if (formik.values.customAmount && Number(formik.values.customAmount) > 999999) {
        enqueueSnackbar("Please enter a valid donation amount (max $999,999)", { variant: "warning" });
        return false;
      }
      if (formik.values.tributeCheck) {
        if (!formik.values.tribute_type?.id) {
          enqueueSnackbar("Please select a tribute type", { variant: "warning" });
          return false;
        }
        if (!formik.values.donor_name?.trim()) {
          enqueueSnackbar("Please enter the honoree name", { variant: "warning" });
          return false;
        }
        if (formik.values.showAcknowledgement) {
          for (const r of formik.values.acknowledgements) {
            if (!r.recipient_name?.trim() || !r.email?.trim()) {
              enqueueSnackbar("Please fill all acknowledgement recipient fields", { variant: "warning" });
              return false;
            }
          }
        }
      }
      return true;
    }
    if (step === 1) {
      if (!formik.values.first_name?.trim()) {
        enqueueSnackbar("Please enter your first name", { variant: "warning" });
        return false;
      }
      if (!formik.values.last_name?.trim()) {
        enqueueSnackbar("Please enter your last name", { variant: "warning" });
        return false;
      }
      if (!formik.values.receipt_email?.trim()) {
        enqueueSnackbar("Please enter your email address", { variant: "warning" });
        return false;
      }
      return true;
    }
    return true;
  };

  // ── Validation (single-page) ─────────────────────────────────────────────
  const validateAll = () => {
    if (!finalAmount) {
      enqueueSnackbar("Please select or enter a donation amount", { variant: "warning" });
      return false;
    }
    if (formik.values.customAmount && Number(formik.values.customAmount) < minimumAmount) {
      enqueueSnackbar(`Minimum custom amount is ${fmtCurrency(minimumAmount, 0)}`, { variant: "warning" });
      return false;
    }
    if (formik.values.customAmount && Number(formik.values.customAmount) > 999999) {
      enqueueSnackbar("Please enter a valid donation amount (max $999,999)", { variant: "warning" });
      return false;
    }
    if (formik.values.tributeCheck) {
      if (!formik.values.tribute_type?.id) {
        enqueueSnackbar("Please select a tribute type", { variant: "warning" });
        return false;
      }
      if (!formik.values.donor_name?.trim()) {
        enqueueSnackbar("Please enter the honoree name", { variant: "warning" });
        return false;
      }
      if (formik.values.showAcknowledgement) {
        for (const r of formik.values.acknowledgements) {
          if (!r.recipient_name?.trim() || !r.email?.trim()) {
            enqueueSnackbar("Please fill all acknowledgement recipient fields", { variant: "warning" });
            return false;
          }
        }
      }
    }
    if (!formik.values.first_name?.trim()) {
      enqueueSnackbar("Please enter your first name", { variant: "warning" });
      return false;
    }
    if (!formik.values.last_name?.trim()) {
      enqueueSnackbar("Please enter your last name", { variant: "warning" });
      return false;
    }
    if (!formik.values.receipt_email?.trim()) {
      enqueueSnackbar("Please enter your email address", { variant: "warning" });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setStep(s => s + 1);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  // tranzilaResult is passed when the Tranzila iframe completes payment
  const handleSubmit = async (tranzilaResult = null) => {
    if (isSingle && !validateAll()) return;

    const isBillMyAccount = formik.values.paymentMethod === "bill";
    const isTranzilaIframe = !isAuth && isTranzila && !!tranzilaResult;

    if (!isAuth && !isBillMyAccount && !isTranzilaIframe && !recaptchaRef.current?.getValue()) {
      enqueueSnackbar("Please complete the captcha", { variant: "warning" });
      return;
    }

    let stripeToken = null;
    if (isStripe && !isBillMyAccount && !isAuth && !isTranzilaIframe) {
      try {
        stripeToken = await stripeRef.current?.handleGetToken();
        if (!stripeToken) {
          enqueueSnackbar("Could not process card — please check your details", { variant: "error" });
          return;
        }
      } catch {
        enqueueSnackbar("Card error — please try again", { variant: "error" });
        return;
      }
    }

    if (isAuth && !isBillMyAccount && !selectedCard) {
      enqueueSnackbar("Please select a card", { variant: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const v = formik.values;

      const paperAddress = v.sendPaper ? {
        location: { formattedAddress: v.addressInfo?.formattedAddress, place_id: v.addressInfo?.place_id },
        city: v.city,
        state: v.state,
        zip_code: v.zip_code,
        addressLine2: v.address2 || undefined,
      } : undefined;

      const tribute_info = {
        donorName: `${v.first_name} ${v.last_name}`.trim(),
        email: v.receipt_email,
        anonymous: v.anonymous === true,
        sendPaper: v.sendPaper,
        address: paperAddress,
        ...(v.tributeCheck && {
          type: v.tribute_type?.id,
          name: v.donor_name,
          message: v.note || undefined,
          acknowledgment: v.showAcknowledgement,
          recipients: v.showAcknowledgement ? v.acknowledgements : undefined,
          is_filled: true,
        }),
      };

      const useAccount = isAuth || isBillMyAccount;
      const payload = {
        isUser: useAccount,
        organization: campaign.org_id,
        ...(useAccount
          ? { Account: { id: memberAccountId, name: memberAccountName } }
          : {
              accountDetails: {
                first_name: v.first_name,
                last_name: v.last_name,
                primary_email_address: v.receipt_email,
                addresses: v.sendPaper
                  ? [{ location: v.addressInfo, city: v.city, state: v.state, zip_code: v.zip_code, addressLine2: v.address2 }]
                  : [],
              },
            }),
        token: stripeToken || undefined,
        cardId: (isAuth && !isBillMyAccount && selectedCard) ? selectedCard : undefined,
        tranzilaChargeResult: tranzilaResult || undefined,
        transactions: [
          {
            Ledger: { id: null, name: "Campaign Donation", account_receivable: null },
            SubLedger: {},
            charge_type: "donation",
            total_amount: finalAmount,
            ...(v.recurringInterval !== "one-time" && {
              isRecurring: true,
              frequency: v.recurringInterval,
            }),
          },
        ],
        tribute_info,
        coverFee: isBillMyAccount ? false : !!v.coverFee,
        billMyAccount: isBillMyAccount,
        paymentMethodId: isBillMyAccount ? undefined : (paymentConfig.id || undefined),
      };

      const res = await FundraisingAPI.donateToCampaign(campaign.slug, payload);
      setDonationResult(res);
      setSuccess(true);
    } catch (err) {
      enqueueSnackbar(
        err?.data?.message || err?.message || "Donation failed — please try again",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ───────────────────────────────────────────────────────
  if (success) {
    const handleDone = () => {
      if (onDonated) onDonated(donationResult);
      onClose();
    };
    const wasBilled = formik.values.paymentMethod === "bill";
    return (
      <div className="dm-overlay">
        <div className="dm" onClick={e => e.stopPropagation()}>
          <div className="dm-success">
            <SucessIcon />
            {wasBilled ? (
              <>
                <h2 className="dm-success__title">Thank You!</h2>
                <p className="dm-success__msg">
                  Thank you, your account has been billed for{" "}
                  <strong>{fmtCurrency(finalAmount, 0)}</strong> to{" "}
                  <strong>{campaign.title}</strong>.
                </p>
              </>
            ) : (
              <>
                <h2 className="dm-success__title">Payment Succeeded!</h2>
                <p className="dm-success__msg">
                  Thank you! Your donation of <strong>{fmtCurrency(finalAmount, 0)}</strong> to{" "}
                  <strong>{campaign.title}</strong> has been processed successfully.
                </p>
              </>
            )}
            <button className="dm-btn dm-btn--primary dm-success__done" onClick={handleDone}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const recurringLabel = { "one-time": null, month: "/mo", year: "/yr" };

  // ── Reusable amount section ───────────────────────────────────────────────
  const renderAmount = () => (
    <>
      <div className="dm-recurring-toggle">
        {[
          { v: "one-time", label: "One-time" },
          { v: "month",    label: "Monthly" },
          { v: "year",     label: "Annually" },
        ].map(opt => (
          <button
            key={opt.v}
            type="button"
            className={`dm-recurring-opt${formik.values.recurringInterval === opt.v ? " dm-recurring-opt--active" : ""}`}
            onClick={() => {
              formik.setFieldValue("recurringInterval", opt.v);
              if (opt.v !== "one-time" && formik.values.paymentMethod === "bill") {
                formik.setFieldValue("paymentMethod", "card");
              }
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <p className="dm-section-label">Choose an amount</p>
      <div className="dm-presets">
        {presets.map(a => (
          <button
            key={a}
            className={`dm-preset ${formik.values.presetAmount === a && !formik.values.customAmount ? "dm-preset--selected" : ""}`}
            onClick={() => { formik.setFieldValue("presetAmount", a); formik.setFieldValue("customAmount", ""); }}
          >
            {fmtCurrency(a, 0)}{recurringLabel[formik.values.recurringInterval] || ""}
          </button>
        ))}
      </div>
      {allowOpenAmount && (
        <div className="dm-custom-amount">
          <span className="dm-custom-amount__symbol">{currSymbol}</span>
          <input
            type="number"
            placeholder="Other amount"
            autoComplete="off"
            min={minimumAmount}
            max={999999}
            value={formik.values.customAmount}
            onChange={e => {
              const val = e.target.value;
              const clamped = val === "" ? "" : String(Math.min(Number(val), 999999));
              formik.setFieldValue("customAmount", clamped);
              formik.setFieldValue("presetAmount", "");
            }}
          />
        </div>
      )}
      {hasFee && finalAmount > 0 && (
        <label className="dm-fee-check">
          <input
            type="checkbox"
            className="dm-checkbox"
            checked={formik.values.coverFee}
            onChange={e => formik.setFieldValue("coverFee", e.target.checked)}
          />
          <span>
            Cover the {paymentConfig.convenienceFeeInfo.type === "%" ? `${paymentConfig.convenienceFeeInfo.amount}%` : fmtCurrency(paymentConfig.convenienceFeeInfo.amount)} processing fee
            {" "}so the organization gets 100% — adds <strong>{fmtCurrency(fee)}</strong>
          </span>
        </label>
      )}
      {finalAmount > 0 && !isSingle && (
        <div className="dm-amount-summary">
          Total: <strong>{fmtCurrency(total)}</strong>
        </div>
      )}
      <div className="dm-tribute-toggle">
        <label className="dm-check-label">
          <input
            type="checkbox"
            className="dm-checkbox"
            checked={formik.values.tributeCheck}
            onChange={e => formik.setFieldValue("tributeCheck", e.target.checked)}
          />
          Give in honor or memory of someone
        </label>
      </div>
      {formik.values.tributeCheck && (
        <div className="dm-tribute">
          <TributeInformation formik={formik} data={[]} />
        </div>
      )}
    </>
  );

  // ── Reusable donor details section ────────────────────────────────────────
  const renderDetails = () => (
    <>
      {isAuth ? (
        <div className="dm-login-prompt">
          <span>Logged in as <strong>{(name => name && name !== "undefined" ? name : "Member")(localStorage.getItem("accountName"))}</strong></span>
          <button type="button" className="dm-login-prompt__btn dm-login-prompt__btn--logout" onClick={() => {
            localStorage.clear();
            setIsAuth(false);
            formik.setFieldValue("first_name", "");
            formik.setFieldValue("last_name", "");
            formik.setFieldValue("receipt_email", "");
            formik.setFieldValue("paymentMethod", "card");
            setSelectedCard(null);
          }}>
            Logout
          </button>
        </div>
      ) : (
        <div className="dm-login-prompt">
          <span>Already a member?</span>
          <button type="button" className="dm-login-prompt__btn" onClick={() => setLoginOpen(true)}>
            Login
          </button>
        </div>
      )}
      {campaign?.allow_anonymous && (
        <label className="dm-check-label dm-check-label--anon">
          <input
            type="checkbox"
            className="dm-checkbox"
            checked={formik.values.anonymous}
            onChange={e => {
              formik.setFieldValue("anonymous", e.target.checked);
              if (e.target.checked && formik.values.paymentMethod === "bill") {
                formik.setFieldValue("paymentMethod", "card");
              }
            }}
          />
          Hide my name
        </label>
      )}
      <div className="dm-row">
        <div className="dm-field">
          <label>First name <span className="dm-req">*</span></label>
          <input
            type="text"
            value={formik.values.first_name}
            onChange={e => formik.setFieldValue("first_name", e.target.value)}
            placeholder="First name"
          />
        </div>
        <div className="dm-field">
          <label>Last name <span className="dm-req">*</span></label>
          <input
            type="text"
            value={formik.values.last_name}
            onChange={e => formik.setFieldValue("last_name", e.target.value)}
            placeholder="Last name"
          />
        </div>
      </div>
      <div className="dm-field">
        <label>Email <span className="dm-req">*</span></label>
        <input
          type="email"
          value={formik.values.receipt_email}
          onChange={e => formik.setFieldValue("receipt_email", e.target.value)}
          placeholder="your@email.com"
        />
        <span className="dm-hint">A receipt will be sent to this address</span>
      </div>
      <label className="dm-check-label">
        <input
          type="checkbox"
          className="dm-checkbox"
          checked={formik.values.sendPaper}
          onChange={e => formik.setFieldValue("sendPaper", e.target.checked)}
        />
        Send a paper receipt
      </label>
      {formik.values.sendPaper && (
        <div className="dm-address">
          <div className="dm-field">
            <label>Address <span className="dm-req">*</span></label>
            <FindMyCity
              handleChange={async data => {
                formik.setFieldValue("addressInfo", {
                  formattedAddress: data?.label,
                  place_id: data?.value?.place_id,
                });
                try {
                  const res = await axios(`https://maps.googleapis.com/maps/api/geocode/json?place_id=${data?.value?.place_id}&key=AIzaSyCAt0TjvU_m8ufUMYn2EXi9mteHSOUB7uA`);
                  const comps = res.data.results[0]?.address_components || [];
                  const zip = comps.find(c => c.types[0] === "postal_code")?.short_name || "";
                  formik.setFieldValue("zip_code", zip);
                  const terms = data?.value?.terms || [];
                  formik.setFieldValue("city",  terms.length === 3 ? terms[1]?.value : terms[2]?.value);
                  formik.setFieldValue("state", terms.length === 3 ? terms[2]?.value : terms[3]?.value);
                } catch (_) {}
              }}
              value={{
                label: formik.values.addressInfo?.formattedAddress
                  ? formik.values.addressInfo.formattedAddress.split(",")[0]
                  : "Enter Address",
                value: formik.values.addressInfo?.place_id || "",
              }}
            />
          </div>
          <div className="dm-field">
            <label>Address 2</label>
            <input
              type="text"
              className="dm-input"
              placeholder="Apt., Suite, Unit"
              value={formik.values.address2}
              onChange={e => formik.setFieldValue("address2", e.target.value)}
            />
          </div>
          <div className="dm-row dm-row--3">
            <div className="dm-field">
              <label>City <span className="dm-req">*</span></label>
              <input type="text" className="dm-input" placeholder="City"
                value={formik.values.city}
                onChange={e => formik.setFieldValue("city", e.target.value)} />
            </div>
            <div className="dm-field">
              <label>State <span className="dm-req">*</span></label>
              <input type="text" className="dm-input" placeholder="State"
                value={formik.values.state}
                onChange={e => formik.setFieldValue("state", e.target.value)} />
            </div>
            <div className="dm-field">
              <label>Zip <span className="dm-req">*</span></label>
              <input type="text" className="dm-input" placeholder="Zip"
                value={formik.values.zip_code}
                onChange={e => formik.setFieldValue("zip_code", e.target.value)} />
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ── Reusable payment section ──────────────────────────────────────────────
  const renderPayment = () => {
    const isBill = formik.values.paymentMethod === "bill";
    return (
      <>
        {canBillAccount && !formik.values.anonymous && formik.values.recurringInterval === "one-time" && (
          <div className="dm-pay-tabs">
            <button
              type="button"
              className={`dm-pay-tab${!isBill ? " dm-pay-tab--active" : ""}`}
              onClick={() => formik.setFieldValue("paymentMethod", "card")}
            >
              <CardIcon /> Pay by card
            </button>
            <button
              type="button"
              className={`dm-pay-tab${isBill ? " dm-pay-tab--active" : ""}`}
              onClick={() => formik.setFieldValue("paymentMethod", "bill")}
            >
              <BillIcon /> Bill my account
            </button>
          </div>
        )}

        {isBill ? (
          <div className="dm-bill-info">
            <BillIcon />
            <div>
              <strong>Bill my account</strong>
              <p>This donation will be added to your account balance. You can pay it through your member portal.</p>
            </div>
          </div>
        ) : isAuth ? (
          <div className="dm-stripe-wrap">
            <p className="dm-section-label">Select a card</p>
            <ManageCreditsCard
              isStripe={isStripe}
              isTranzila={isTranzila}
              currentPaymentMethod={{
                paymentGateway: {
                  provider: isTranzila ? "Tranzila" : isStripe ? "Stripe" : "",
                  keys: {
                    publicKey: paymentConfig.publicKey,
                    terminal_name: paymentConfig.terminalName,
                  },
                },
              }}
              setSelectedCard={setSelectedCard}
              selectedCard={selectedCard}
              publicKey={paymentConfig.publicKey}
              onCardCurrencyChange={(cur) => setTranzilaCurrency(cur || "ILS")}
            />
          </div>
        ) : isTranzila ? (
          <div className="dm-stripe-wrap">
            <p className="dm-section-label">Card information</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {["ILS", "USD"].map((cur) => (
                <button
                  key={cur}
                  type="button"
                  onClick={() => setTranzilaCurrency(cur)}
                  style={{
                    padding: "4px 14px",
                    borderRadius: 6,
                    border: `2px solid ${tranzilaCurrency === cur ? "#1a73e8" : "#ccc"}`,
                    background: tranzilaCurrency === cur ? "#1a73e8" : "#fff",
                    color: tranzilaCurrency === cur ? "#fff" : "#333",
                    fontWeight: tranzilaCurrency === cur ? FontWeights.SemiBold : FontWeights.Regular,
                    cursor: "pointer",
                  }}
                >
                  {cur === "ILS" ? "₪ ILS" : "$ USD"}
                </button>
              ))}
            </div>
            <TranzilaCard
              onSaveCard={() => {}}
              onCharged={handleSubmit}
              loading={submitting}
              setLoading={() => {}}
              saveCard={false}
              currency={tranzilaCurrency}
              amount={total}
              terminalName={paymentConfig.terminalName}
              email={formik.values.receipt_email}
              contactName={`${formik.values.first_name || ""} ${formik.values.last_name || ""}`.trim()}
              paymentMethodId={paymentConfig.id}
              orgId={campaign.org_id}
            />
          </div>
        ) : isStripe ? (
          <div className="dm-stripe-wrap">
            <p className="dm-section-label">Card information</p>
            <StripeCard
              ref={stripeRef}
              publicKey={paymentConfig.publicKey}
              loading={submitting}
              setLoading={() => {}}
            />
          </div>
        ) : (
          <div className="dm-manual-card">
            <p className="dm-section-label">Card information</p>
            <div className="dm-field">
              <label>Card number</label>
              <input type="text" placeholder="1234 5678 9012 3456" maxLength={19} />
            </div>
            <div className="dm-row">
              <div className="dm-field">
                <label>Expiry</label>
                <input type="text" placeholder="MM/YY" maxLength={5} />
              </div>
              <div className="dm-field">
                <label>CVC</label>
                <input type="text" placeholder="CVC" maxLength={4} />
              </div>
              <div className="dm-field">
                <label>ZIP</label>
                <input type="text" placeholder="ZIP" maxLength={10} />
              </div>
            </div>
          </div>
        )}

        {!isAuth && !isBill && !isTranzila && (
          <div className="dm-captcha">
            <Recaptcha recaptchaRef={recaptchaRef} />
          </div>
        )}
        {!isBill && (
          <div className="dm-secure-note">
            <LockIcon /> Secured &amp; encrypted payment
          </div>
        )}
      </>
    );
  };

  return (
    <div className="dm-overlay" onClick={onClose}>
      <div className="dm" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dm-header">
          <div>
            <h2 className="dm-header__title">Donate to {campaign.title}</h2>
            {!isSingle && (
              <div className="dm-stepper">
                {STEPS.map((label, i) => (
                  <React.Fragment key={label}>
                    <div className={`dm-step ${i === step ? "dm-step--active" : ""} ${i < step ? "dm-step--done" : ""}`}>
                      <span className="dm-step__dot">{i < step ? "✓" : i + 1}</span>
                      <span className="dm-step__label">{label}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`dm-step__line ${i < step ? "dm-step__line--done" : ""}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          <button className="dm-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="dm-body">
          {isSingle ? (
            <div className="dm-step-content">
              {renderAmount()}
              <div className="dm-divider" />
              <p className="dm-section-label">Your information</p>
              {renderDetails()}
              <div className="dm-divider" />
              <p className="dm-section-label">Payment</p>
              {finalAmount > 0 && (
                <div className="dm-pay-summary">
                  <span>Donating to <strong>{campaign.title}</strong></span>
                  <span className="dm-pay-summary__total">{fmtCurrency(total)}</span>
                </div>
              )}
              {renderPayment()}
            </div>
          ) : (
            <>
              {/* STEP 0: Amount + Tribute */}
              {step === 0 && (
                <div className="dm-step-content">
                  {renderAmount()}
                </div>
              )}

              {/* STEP 1: Donor details */}
              {step === 1 && (
                <div className="dm-step-content">
                  {renderDetails()}
                </div>
              )}

              {/* STEP 2: Payment */}
              {step === 2 && (
                <div className="dm-step-content">
                  <div className="dm-pay-summary">
                    <span>Donating to <strong>{campaign.title}</strong></span>
                    <span className="dm-pay-summary__total">{fmtCardCurrency(total)}</span>
                  </div>
                  {renderPayment()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — Tranzila iframe provides its own Pay Now button for unauthenticated users */}
        {(() => {
          const tranzilaIframeActive = !isAuth && isTranzila && formik.values.paymentMethod !== "bill";
          if (isSingle) {
            if (tranzilaIframeActive) return null;
            return (
              <div className="dm-footer">
                <button
                  className="dm-btn dm-btn--primary dm-btn--donate"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  {submitting ? <span className="dm-spinner" /> : donateLabel(total, formik.values.recurringInterval)}
                </button>
              </div>
            );
          }
          return (
            <div className="dm-footer">
              {step > 0 && (
                <button className="dm-btn dm-btn--back" onClick={() => setStep(s => s - 1)}>
                  ← Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button className="dm-btn dm-btn--primary" onClick={handleNext}>
                  Continue →
                </button>
              ) : !tranzilaIframeActive ? (
                <button
                  className="dm-btn dm-btn--primary dm-btn--donate"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  {submitting ? <span className="dm-spinner" /> : donateLabel(total, formik.values.recurringInterval)}
                </button>
              ) : null}
            </div>
          );
        })()}

      </div>

      {loginOpen && (
        <LoginModal
          orgLogo={campaign.org_logo_url}
          onClose={() => setLoginOpen(false)}
          onSuccess={() => {
            const info = getStoredAccountInfo();
            formik.setFieldValue("first_name", info.firstName || "");
            formik.setFieldValue("last_name", info.lastName || "");
            formik.setFieldValue("receipt_email", info.primary_email_address || "");
            setIsAuth(isValidAccountId(localStorage.getItem("accountID")));
            setLoginOpen(false);
          }}
        />
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const CardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const BillIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="12" y2="17" />
  </svg>
);

export default DonateModal;
